import { Component, OnInit, OnDestroy } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { FormBuilder, UntypedFormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Message, SpinnerService } from "shared-components";
import { ExpedicionesContextService } from "../../context/expediciones-context.service";
import { ExpedicionesService } from "../../services/expediciones.service";
import { Router } from "@angular/router";

@Component({
  selector: "app-mantenimiento-certificados",
  templateUrl: "./mantenimiento-certificados.component.html",
})
export class MantenimientoCertificadosComponent implements OnInit, OnDestroy {
  private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

  public certificados;
  public certificadoId: number;

  // Filtros
  public filtersVisible = false;
  public filtersForm: UntypedFormGroup;
  public titulares;
  public denominaciones;
  public estados;
  private _filters;
  private toCloseSpinner = 0;

  public dropdownInfo = -1;
  public dropdownSection: number;
  public pestanaActiva = new Array<number>();

  // Paginación
  public itemsPerPage = 10;
  public currentPage = 0;
  public totalItems: number;
  public readonly visiblePages = 3;

  // Ordenación
  public orderBy: string;
  public columnId: number;

  // Modales
  public deleteModal = false;
  public cancelModal = false;
  public alertModal = false;

  public pdfModal = false;
  public base64Certificado;
  public pdfMode = ""; // sign o view
  private pdfFileName = "";

  public showError: boolean;
  public mensajes = new Array<Message>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _translate: TranslateService,
    private readonly _sessionContext: SessionContextService,
    private readonly _certificadosContext: ExpedicionesContextService,
    private readonly _fb: FormBuilder,
    private readonly _spinnerService: SpinnerService,
    private readonly _expedicionesService: ExpedicionesService,
    private readonly _router: Router,
  ) {}

  ngOnInit(): void {
    this.filtersForm = this._fb.group({
      titular: [""],
      denominacion: [""],
      finca: [""],
      matricula: [""],
      estado: [""],
      certificadoNum: [""],
      fechaInicio: [""],
      fechaFin: [""],
      dateType: [true],
    });

    this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
    this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

    this.getCertificados();
    this.getTitulares();
    this.getDenominaciones();
    this.getEstados();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  public get role() {
      return this._sessionContext.sessionUser.role;
  }

  public get defaultLanguage() {
    return this._translate.getDefaultLang();
  }

  public get isSmallScreen() {
    return this._sessionContext.isSmallScreen();
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
      this._spinnerService.hideSpinner();
    }
  }

  private getCertificados() {
    if (this._certificadosContext.mantenimientoCertificadosFilters) {
      this._filters =
        this._certificadosContext.mantenimientoCertificadosFilters;

      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._expedicionesService
        .getCertificados(this._filters)
        .toPromise()
        .finally(() => {
          this.checkCloseSpinner();
        })
        .then(
          (resp) => {
            this.dropdownSection = -1;
            this.certificados = resp.lista;
            this.totalItems = resp.paginacionMeta.totalCount;
          },
          (error) => {
            this.showError = true;
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          },
        );
    } else {
      this.applyFilters();
    }
  }

  private getTitulares() {
    let filters = {};
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._expedicionesService
      .getEtiquetadores(filters)
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        if (resp.lista.length > 0) {
          this.titulares = resp.lista;
          this.filtersForm.get("titular").enable();

          if (this.titulares.length === 1) {
            this.filtersForm.get("titular").patchValue(this.titulares[0]);
          }
        }
      });
  }

  private getDenominaciones() {
    let filters = {};
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._expedicionesService
      .getDenominacionesByPais(filters)
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        if (resp.lista.length > 0) {
          this.denominaciones = resp.lista;
          this.filtersForm.get("denominacion").enable();

          if (this.denominaciones.length === 1) {
            this.filtersForm
              .get("denominacion")
              .patchValue(this.denominaciones[0]);
          }
        }
      });
  }

  private getEstados() {
    const status: Array<any> = [];
    let id = 1;

    this._translate.get("global.tipoEstado").subscribe((res) => {
      Object.keys(res).forEach((key) => {
        const value = res[key];
        status.push({
          des: value,
          id: id,
        });
        id += 1;
      });
    });

    this.estados = status;
  }

  public resetForm() {
    this.filtersForm.reset();
    this.filtersForm.get("dateType").patchValue(true);
  }

  /**
   * Para habilitar/deshabilitar botón "Limpiar filtros"
   */
  public checkValuesForm(): boolean {
    return this.filtersForm.get("titular").value ||
      this.filtersForm.get("denominacion").value ||
      this.filtersForm.get("finca").value ||
      this.filtersForm.get("matricula").value ||
      this.filtersForm.get("estado").value ||
      this.filtersForm.get("certificadoNum").value ||
      this.filtersForm.get("fechaInicio").value ||
      this.filtersForm.get("fechaFin").value ||
      !this.filtersForm.get("dateType").value
      ? true
      : false;
  }

  private checkDates(inicio, fin) {
    const inicioD = new Date(inicio);
    const finD = new Date(fin);
    return inicioD <= finD;
  }

  private clearDatesValidators() {
    if (
      this.filtersForm.get("fechaFin").errors &&
      this.filtersForm.get("fechaFin").errors.includes("Invalid date")
    ) {
      this.filtersForm.get("fechaFin").clearValidators();
      this.filtersForm.get("fechaFin").updateValueAndValidity();
    }
    if (
      this.filtersForm.get("fechaInicio").errors &&
      this.filtersForm.get("fechaInicio").errors.includes("Invalid date")
    ) {
      this.filtersForm.get("fechaInicio").clearValidators();
      this.filtersForm.get("fechaInicio").updateValueAndValidity();
    }
  }

  private validDates() {
    if (
      this.filtersForm.get("fechaInicio").value &&
      this.filtersForm.get("fechaFin").value
    ) {
      if (
        this.checkDates(
          this.filtersForm.get("fechaInicio").value,
          this.filtersForm.get("fechaFin").value,
        )
      ) {
        this.clearDatesValidators();
      } else {
        this.filtersForm.get("fechaInicio").setErrors(["Invalid date"]);
        this.filtersForm.get("fechaFin").setErrors(["Invalid date"]);
      }
      if (
        this.filtersForm.get("fechaInicio").invalid ||
        this.filtersForm.get("fechaFin").invalid
      ) {
        this.showError = true;
        this.mensajes.push({
          class: "error",
          message: "global.fecha.error.finAnteriorInicio",
        });
      } else {
        this.showError = false;
        this.mensajes = [];
      }
    } else {
      this.clearDatesValidators();
      this.showError = false;
      this.mensajes = [];
    }
  }

  public filtrar() {
    this.validDates();
    if (this.filtersForm.valid) {
      this.applyFilters();
    }
  }

  public applyFilters() {
    this.currentPage = 0;
    this._certificadosContext.mantenimientoCertificadosFilters = {
      CargaRegistroEnUso: true,
      etiquetadorId: this.filtersForm.get("titular").value?.id,
      denominacionId: this.filtersForm.get("denominacion").value?.id,
      desFinca: this.filtersForm.get("finca").value,
      matricula: this.filtersForm.get("matricula").value,
      estadoId: this.filtersForm.get("estado").value?.id,
      nCertificadoExpedicion: this.filtersForm.get("certificadoNum").value,
      fechaInicioVigencia: this.filtersForm.get("fechaInicio").value,
      fechaFinVigencia: this.filtersForm.get("fechaFin").value,
      esFechaInicioVigencia: this.filtersForm.get("dateType").value,
      numPagina: this.currentPage,
      tamPagina: this.itemsPerPage,
    };

    this.getCertificados();
  }

  public showDeleteModal(certificadoId: number) {
    this.certificadoId = certificadoId;
    this.deleteModal = true;
  }

  public showCancelModal(certificadoId: number) {
    this.certificadoId = certificadoId;
    this.cancelModal = true;
  }

  public closeDeleteCancelModal() {
    this.deleteModal = false;
    this.cancelModal = false;
  }

  public deleteCancelCertificate() {
    this._spinnerService.showSpinner();
    if (this.deleteModal) {
      this._expedicionesService
        .deleteCertificadoExpedicion(this.certificadoId)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then(
          (resp) => {
            this.showError = true;
            this.mensajes.push({
              class: "success",
              message: "global.success.itemDeleted",
            });
            this.getCertificados();
          },
          (error) => {
            this.showError = true;
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          },
        );
    } else if (this.cancelModal) {
      this.updateStatus(this.certificadoId, 4);
    }

    this.closeDeleteCancelModal();
  }

  public updateStatus(certificateId: number, statusId: number) {
    let filters = {
      certificadoExpedicionId: certificateId,
      estadoId: statusId,
    };

    this._spinnerService.showSpinner();
    this._expedicionesService
      .updateEstadosCertificado(filters)
      .toPromise()
      .finally(() => {
        this._spinnerService.hideSpinner();
      })
      .then(
        (resp) => {
          this.showError = true;
          this.mensajes.push({
            class: "success",
            message: "global.success.itemUpdated",
          });
          this.getCertificados();
        },
        (error) => {
          this.showError = true;
          this.mensajes.push({
            class: "error",
            message: "global.error.noResponse",
          });
        },
      );
  }

  /**
   * Para los iconos de los Certificados
   */
  public getCheckIconColor(estadoId: number) {
    switch (estadoId) {
      case 1: // Pendiente
        return { color: "#005ccc" };
      case 2: // Válido
        return { color: "#2e9900" };
      case 3: // Inválido
        return { color: "#e61717" };
      case 4: // Cancelado
        return { color: "#cc7614" };
      case 5: // Expirado
        return { color: "#d4d4d4" };
    }
  }

  public getCheckIconTitle(estadoId: number) {
    switch (estadoId) {
      case 1:
        return this._translate.instant("global.tipoEstado.pending");
      case 2:
        return this._translate.instant("global.tipoEstado.valid");
      case 3:
        return this._translate.instant("global.tipoEstado.invalid");
      case 4:
        return this._translate.instant("global.tipoEstado.canceled");
      case 5:
        return this._translate.instant("global.tipoEstado.expired");
    }
  }

  public showSection(i) {
    this.dropdownSection = this.dropdownSection === i ? -1 : i;
    this.pestanaActiva[i] = 1;
    // Desplazamiento a la izquierda para tablet (mismo tamaño máx. que en table.scss)
    if (this.dropdownSection !== -1 && window.innerWidth <= 1199) {
      // Retardo: en caso de que haya otro detalle abierto
      setTimeout(() => {
        document.querySelector("table")?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }, 50);
    }
  }

  public toggleDropdownInfo(index: number): void {
    this.dropdownInfo = this.dropdownInfo === index ? -1 : index;
  }
  
  public getBlockchainLink(txId: string): string {
      const baseUrl = 'https://explore.vechain.org/';
      return txId ? `${baseUrl}transactions/${txId}` : baseUrl;
  }

  public showExpedition(index: number) {
    this.pestanaActiva[index] = 1;
  }

  public showStatus(index: number) {
    this.pestanaActiva[index] = 2;
  }

  public toOrderBy(columnFilter: string, colId: number) {
    this.columnId = colId;

    if (
      this.certificados &&
      this.certificados.length > 0 &&
      this._certificadosContext.mantenimientoCertificadosFilters
    ) {
      this._filters =
        this._certificadosContext.mantenimientoCertificadosFilters;
      const isAscending = !this.orderBy || this.orderBy.includes("DESC");
      this.orderBy = isAscending ? "ASC" + colId : "DESC" + colId;
      this._filters.orderItems = [
        {
          orderProperty: columnFilter,
          orderByDescending: !isAscending,
        },
      ];

      this.getCertificados();
    }
  }

  public getIcon(prop: string, colId: number) {
    let iconClass = "fa fa-sort";
    if (
      this._filters &&
      this._filters.orderItems &&
      this._filters.orderItems[0].orderProperty.indexOf(prop) != -1 &&
      this.columnId === colId
    ) {
      iconClass =
        this._filters.orderItems[0].orderByDescending === true
          ? "fa fa-sort-down"
          : "fa fa-sort-up";
    }
    return iconClass;
  }

  public pageChange(page) {
    this.dropdownInfo = -1;
    this._certificadosContext.mantenimientoCertificadosFilters.numPagina =
      this.currentPage = page - 1;
    this.getCertificados();
  }

  public itemsPerPageChange(items) {
    this.dropdownInfo = -1;
    this._certificadosContext.mantenimientoCertificadosFilters.numPagina =
      this.currentPage = 0;
    this._certificadosContext.mantenimientoCertificadosFilters.tamPagina =
      this.itemsPerPage = items;
    this.getCertificados();
  }

  public showPdfModal(id: number, mode: string) {
    this.certificadoId = id;
    this.pdfMode = mode;

    this._spinnerService.showSpinner();
    this._expedicionesService
      .getCertificadoPdf(this.certificadoId)
      .toPromise()
      .finally(() => {
        this._spinnerService.hideSpinner();
      })
      .then(
        (resp) => {
          this.base64Certificado = resp.base64Archivo;
          this.pdfFileName = resp.nombreArchivo;
          this.pdfModal = true;
        },
        (error) => {
          this.showError = true;
          this.mensajes.push({
            class: "error",
            message: "global.error.noResponse",
          });
        },
      );
  }

  public closeSignaturePdfModal() {
    this.pdfModal = false;
  }

  private signCertificado() {
    const filters = {
      certificadoExpedicionId: this.certificadoId,
    };

    this._spinnerService.showSpinner();
    this._expedicionesService
      .signCertificadoPdf(filters)
      .toPromise()
      .finally(() => {
        this._spinnerService.hideSpinner();
      })
      .then(
        (resp) => {
          this.pdfModal = false;
          this.showError = true;
          this.mensajes.push({
            class: "success",
            message: "global.success.certificadoFirmado",
          });
          this.getCertificados();
        },
        (error) => {
          this.pdfModal = false;
          this.showError = true;

          if (error.status === 420 && error.error.ApiErrorCode === 6250) {
            this.mensajes.push({
              class: "error",
              message: "global.error.signCertificate",
            });
          } else {
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          }
        },
      );
  }

  private downloadCertificado() {
    const downloadLink = document.createElement("a");
    downloadLink.href = "data:application/pdf;base64," + this.base64Certificado;
    downloadLink.download = this.pdfFileName;
    downloadLink.click();
  }

  public signOrDownlowadCertificado() {
    if (this.pdfMode === "sign") {
      this.signCertificado();
    } else {
      this.downloadCertificado();
    }
  }

  public showAlertModal() {
    this.alertModal = true;
  }

  public closeAlertModal() {
    this.alertModal = false;
  }

  public openDocHelp() {
    this._spinnerService.showSpinner();

    this._expedicionesService.getDocHelp('Expeditions_Certificates.pdf').toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then((resp) => {
      localStorage.setItem('base64', resp.base64Archivo);
      const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
      window.open(url, '_blank');
    });
  }

  public trackByCertificadoId(index: number, certificado: { id: number }): number {
    return certificado.id;
  }
}
